<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <!-- Loading state -->
    <div v-if="loadingOrden" class="flex justify-center items-center min-h-screen">
      <div class="text-center">
        <svg class="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-600">Cargando información de la orden...</p>
      </div>
    </div>

    <!-- Main content -->
    <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white shadow rounded-lg mb-8">
        <div class="px-6 py-4">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Generar Factura</h1>
              <p class="text-gray-600">Orden #{{ ordenData?.numero || numeroOrden }}</p>
            </div>
            <button
              @click="$router.push({ name: 'buscar-orden' })"
              class="text-indigo-600 hover:text-indigo-500 flex items-center"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Buscar otra orden
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Información de la orden -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Información de la Orden</h2>
          </div>
          <div class="px-6 py-4">
            <dl class="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-gray-500">Número de Orden</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ ordenData?.numero || numeroOrden }}</dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-gray-500">Fecha</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ formatearFecha(ordenData?.fecha) }}</dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-gray-500">Cliente</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ ordenData?.cliente?.nombre }}</dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-sm font-medium text-gray-500">Total</dt>
                <dd class="mt-1 text-sm text-gray-900 font-semibold">${{ formatearMonto(ordenData?.total) }}</dd>
              </div>
            </dl>

            <!-- Productos -->
            <div class="mt-6">
              <h3 class="text-sm font-medium text-gray-500 mb-3">Productos</h3>
              <div class="space-y-2">
                <div 
                  v-for="producto in ordenData?.productos" 
                  :key="producto.id"
                  class="flex justify-between items-center p-3 bg-gray-50 rounded"
                >
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ producto.nombre }}</p>
                    <p class="text-xs text-gray-500">{{ producto.descripcion }}</p>
                    <p class="text-xs text-gray-500">Cantidad: {{ producto.cantidad }} | Precio unitario: ${{ formatearMonto(producto.precio_unitario) }}</p>
                  </div>
                  <p class="text-sm font-medium text-gray-900">${{ formatearMonto(producto.subtotal) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Aviso de orden ya facturada -->
        <div v-if="ordenYaFacturada" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Factura Existente</h2>
          </div>
          <div class="px-6 py-4">
            <!-- Banner informativo -->
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-md mb-4">
              <div class="flex">
                <svg class="h-5 w-5 text-blue-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-sm text-blue-700">Esta orden ya cuenta con una factura generada.</p>
              </div>
            </div>

            <!-- Datos de la factura existente -->
            <div v-if="facturaExistente" class="bg-gray-50 rounded-md p-4 mb-4 space-y-2">
              <p v-if="facturaExistente.folio" class="text-sm text-gray-700">
                <span class="font-medium">Folio:</span> {{ facturaExistente.folio }}
              </p>
              <p v-if="facturaExistente.fecha" class="text-sm text-gray-700">
                <span class="font-medium">Fecha:</span> {{ formatearFecha(facturaExistente.fecha) }}
              </p>
              <p v-if="facturaExistente.uuid" class="text-sm text-gray-700">
                <span class="font-medium">UUID:</span> {{ facturaExistente.uuid }}
              </p>
            </div>

            <!-- Botones de descarga y reenvío -->
            <div class="space-y-2">
              <button
                @click="descargarPDFExistente"
                :disabled="descargando.pdf"
                class="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                {{ descargando.pdf ? 'Descargando...' : 'Descargar PDF' }}
              </button>
              <div v-if="errorDescarga.pdf" class="text-left mt-1 p-2 bg-red-50 rounded-md">
                <p class="text-sm text-red-600">{{ errorDescarga.pdf }}</p>
                <button @click="descargarPDFExistente" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
              </div>

              <button
                @click="descargarXMLExistente"
                :disabled="descargando.xml"
                class="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                {{ descargando.xml ? 'Descargando...' : 'Descargar XML' }}
              </button>
              <div v-if="errorDescarga.xml" class="text-left mt-1 p-2 bg-red-50 rounded-md">
                <p class="text-sm text-red-600">{{ errorDescarga.xml }}</p>
                <button @click="descargarXMLExistente" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
              </div>

              <button
                @click="enviarEmailExistente"
                :disabled="enviandoEmail"
                class="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                {{ enviandoEmail ? 'Enviando...' : 'Enviar por Email' }}
              </button>
              <p v-if="emailEnviado" class="text-sm text-green-600 mt-1">
                ✓ Factura enviada a {{ ordenData?.cliente?.email || 'correo registrado' }}
              </p>
              <div v-if="errorEmail" class="text-left mt-1 p-2 bg-red-50 rounded-md">
                <p class="text-sm text-red-600">{{ errorEmail }}</p>
                <button @click="enviarEmailExistente" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Formulario de datos fiscales -->
        <div v-if="!ordenYaFacturada" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Datos Fiscales</h2>
            <p class="text-sm text-gray-500">Completa la información para generar tu factura</p>
          </div>
          
          <form @submit.prevent="generarFactura" class="px-6 py-4 space-y-4">
            <!-- Tipo de persona -->
            <div>
              <label class="block text-sm font-medium text-gray-700">Tipo de Persona</label>
              <div class="mt-2 space-x-4">
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    v-model="datosFiscales.tipo_persona"
                    value="fisica"
                    class="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">Persona Física</span>
                </label>
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    v-model="datosFiscales.tipo_persona"
                    value="moral"
                    class="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">Persona Moral</span>
                </label>
              </div>
            </div>

            <!-- RFC -->
            <div>
              <label for="rfc" class="block text-sm font-medium text-gray-700">RFC *</label>
              <input
                id="rfc"
                type="text"
                v-model="datosFiscales.rfc"
                @blur="validarRFC"
                required
                :maxlength="datosFiscales.tipo_persona === 'moral' ? 12 : 13"
                :placeholder="datosFiscales.tipo_persona === 'moral' ? 'XXX010101000' : 'XAXX010101000'"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.rfc }"
              />
              <p v-if="errores.rfc" class="mt-1 text-sm text-red-600">{{ errores.rfc }}</p>
            </div>

            <!-- Razón Social / Nombre -->
            <div>
              <label for="razon_social" class="block text-sm font-medium text-gray-700">
                {{ datosFiscales.tipo_persona === 'fisica' ? 'Nombre Completo' : 'Razón Social' }} *
              </label>
              <input
                id="razon_social"
                type="text"
                v-model="datosFiscales.razon_social"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.razon_social }"
              />
              <p v-if="errores.razon_social" class="mt-1 text-sm text-red-600">{{ errores.razon_social }}</p>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email *</label>
              <input
                id="email"
                type="email"
                v-model="datosFiscales.email"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.email }"
              />
              <p v-if="errores.email" class="mt-1 text-sm text-red-600">{{ errores.email }}</p>
              <p v-else class="mt-1 text-xs text-gray-500">La factura se enviará a este correo</p>
            </div>

            <!-- Código Postal -->
            <div>
              <label for="codigo_postal" class="block text-sm font-medium text-gray-700">Código Postal *</label>
              <input
                id="codigo_postal"
                type="text"
                v-model="datosFiscales.codigo_postal"
                required
                maxlength="5"
                pattern="[0-9]{5}"
                placeholder="12345"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.codigo_postal }"
              />
              <p v-if="errores.codigo_postal" class="mt-1 text-sm text-red-600">{{ errores.codigo_postal }}</p>
            </div>

            <!-- Uso de CFDI -->
            <div>
              <label for="uso_cfdi" class="block text-sm font-medium text-gray-700">Uso de CFDI *</label>
              <select
                id="uso_cfdi"
                v-model="datosFiscales.uso_cfdi"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Selecciona un uso</option>
                <option value="G01">Adquisición de mercancías</option>
                <option value="G02">Devoluciones, descuentos o bonificaciones</option>
                <option value="G03">Gastos en general</option>
                <option value="I01">Construcciones</option>
                <option value="I02">Mobilario y equipo de oficina por inversiones</option>
                <option value="I03">Equipo de transporte</option>
                <option value="I04">Equipo de computo y accesorios</option>
                <option value="I05">Dados, troqueles, moldes, matrices y herramental</option>
                <option value="I06">Comunicaciones telefónicas</option>
                <option value="I07">Comunicaciones satelitales</option>
                <option value="I08">Otra maquinaria y equipo</option>
                <option value="D01">Honorarios médicos, dentales y gastos hospitalarios</option>
                <option value="D02">Gastos médicos por incapacidad o discapacidad</option>
                <option value="D03">Gastos funerales</option>
                <option value="D04">Donativos</option>
                <option value="D05">Intereses reales efectivamente pagados por créditos hipotecarios</option>
                <option value="D06">Aportaciones voluntarias al SAR</option>
                <option value="D07">Primas por seguros de gastos médicos</option>
                <option value="D08">Gastos de transportación escolar obligatoria</option>
                <option value="D09">Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                <option value="D10">Pagos por servicios educativos (colegiaturas)</option>
                <option value="P01">Por definir</option>
              </select>
            </div>

            <!-- Régimen Fiscal -->
            <div>
              <label for="regimen_fiscal" class="block text-sm font-medium text-gray-700">Régimen Fiscal *</label>
              <select
                id="regimen_fiscal"
                v-model="datosFiscales.regimen_fiscal"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Selecciona un régimen</option>
                <option value="601">General de Ley Personas Morales</option>
                <option value="603">Personas Morales con Fines no Lucrativos</option>
                <option value="605">Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                <option value="606">Arrendamiento</option>
                <option value="607">Régimen de Enajenación o Adquisición de Bienes</option>
                <option value="608">Demás ingresos</option>
                <option value="610">Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                <option value="611">Ingresos por Dividendos (socios y accionistas)</option>
                <option value="612">Personas Físicas con Actividades Empresariales y Profesionales</option>
                <option value="614">Ingresos por intereses</option>
                <option value="615">Régimen de los ingresos por obtención de premios</option>
                <option value="616">Sin obligaciones fiscales</option>
                <option value="620">Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                <option value="621">Incorporación Fiscal</option>
                <option value="622">Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                <option value="623">Opcional para Grupos de Sociedades</option>
                <option value="624">Coordinados</option>
                <option value="625">Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                <option value="626">Régimen Simplificado de Confianza</option>
              </select>
            </div>

            <!-- Botones -->
            <div class="flex space-x-4 pt-4">
              <button
                type="submit"
                :disabled="loading || !formularioValido"
                class="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="loading" class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generando Factura...
                </span>
                <span v-else>Generar Factura</span>
              </button>
            </div>

            <!-- Error de generación -->
            <div v-if="errorGeneracion" class="mt-3 p-3 bg-red-50 rounded-md">
              <p class="text-sm text-red-600">{{ errorGeneracion }}</p>
              <button @click="generarFactura" type="button" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal de éxito -->
    <div v-if="facturaGenerada !== null" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3 text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">¡Factura Generada!</h3>
          <div class="mt-2 px-4 py-3">
            <p class="text-sm text-gray-500 mb-2">Tu factura ha sido generada exitosamente.</p>
            <div class="bg-gray-50 rounded-md p-3 text-left space-y-1">
              <p class="text-sm text-gray-700"><span class="font-medium">Folio:</span> {{ facturaGenerada.folio }}</p>
              <p class="text-sm text-gray-700"><span class="font-medium">UUID:</span> {{ facturaGenerada.uuid }}</p>
            </div>
          </div>

          <!-- Botones de descarga -->
          <div class="px-4 py-2 space-y-2">
            <button
              @click="descargarPDF"
              :disabled="descargando.pdf"
              class="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              {{ descargando.pdf ? 'Descargando...' : 'Descargar PDF' }}
            </button>
            <div v-if="errorDescarga.pdf" class="text-left mt-1 p-2 bg-red-50 rounded-md">
              <p class="text-sm text-red-600">{{ errorDescarga.pdf }}</p>
              <button @click="descargarPDF" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
            </div>
            <button
              @click="descargarXML"
              :disabled="descargando.xml"
              class="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              {{ descargando.xml ? 'Descargando...' : 'Descargar XML' }}
            </button>
            <div v-if="errorDescarga.xml" class="text-left mt-1 p-2 bg-red-50 rounded-md">
              <p class="text-sm text-red-600">{{ errorDescarga.xml }}</p>
              <button @click="descargarXML" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
            </div>

            <!-- Enviar por email -->
            <button
              @click="enviarEmail"
              :disabled="enviandoEmail"
              class="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              {{ enviandoEmail ? 'Enviando...' : 'Enviar por Email' }}
            </button>
            <p v-if="emailEnviado" class="text-sm text-green-600 mt-1">
              ✓ Factura enviada a {{ datosFiscales.email }}
            </p>
            <div v-if="errorEmail" class="text-left mt-1 p-2 bg-red-50 rounded-md">
              <p class="text-sm text-red-600">{{ errorEmail }}</p>
              <button @click="enviarEmail" class="text-sm text-red-700 underline hover:text-red-800 font-medium mt-1">Reintentar</button>
            </div>
          </div>

          <div class="px-4 py-3">
            <button
              @click="cerrarModal"
              class="px-4 py-2 bg-indigo-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import facturacionService from '@/Services/facturacion';
import { validarFormatoRFC } from '@/functions/validarRFC';
import { clasificarErrorFacturacion } from '@/functions/clasificarErrorFacturacion';

export default {
  name: 'CrearFactura',
  data() {
    return {
      ordenData: null,
      loadingOrden: true,
      loading: false,
      ordenYaFacturada: false,
      facturaExistente: null,
      facturaGenerada: null,
      emailEnviado: false,
      descargando: { pdf: false, xml: false },
      enviandoEmail: false,
      errorDescarga: { pdf: '', xml: '' },
      errorEmail: '',
      errorGeneracion: '',
      numeroOrden: '',
      datosFiscales: {
        tipo_persona: 'fisica',
        rfc: '',
        razon_social: '',
        email: '',
        codigo_postal: '',
        uso_cfdi: 'G03',
        regimen_fiscal: ''
      },
      errores: {
        rfc: '',
        razon_social: '',
        email: '',
        codigo_postal: '',
        uso_cfdi: '',
        regimen_fiscal: ''
      }
    };
  },
  computed: {
    formularioValido() {
      const rfc = (this.datosFiscales.rfc || '').trim();
      const razonSocial = (this.datosFiscales.razon_social || '').trim();
      const email = (this.datosFiscales.email || '').trim();
      const codigoPostal = (this.datosFiscales.codigo_postal || '').trim();
      const usoCfdi = (this.datosFiscales.uso_cfdi || '').trim();
      const regimenFiscal = (this.datosFiscales.regimen_fiscal || '').trim();

      // Todos los campos obligatorios deben estar presentes
      if (!rfc || !razonSocial || !email || !codigoPostal || !usoCfdi || !regimenFiscal) {
        return false;
      }

      // No debe haber errores de validación de RFC
      if (this.errores.rfc) {
        return false;
      }

      // Validar RFC según tipo de persona usando validarFormatoRFC
      const resultadoRFC = validarFormatoRFC(rfc, this.datosFiscales.tipo_persona);
      if (!resultadoRFC.valido) {
        return false;
      }

      // Código postal debe ser exactamente 5 dígitos
      if (!/^\d{5}$/.test(codigoPostal)) {
        return false;
      }

      // Validación básica de email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return false;
      }

      return true;
    }
  },
  async mounted() {
    this.numeroOrden = this.$route.query.numero || '';
    const ordenId = this.$route.params.ordenId;

    if (!ordenId) {
      this.$router.push({ name: 'buscar-orden' });
      return;
    }

    await this.cargarDatosOrden(ordenId);
  },
  methods: {
    async cargarDatosOrden(ordenId) {
      try {
        this.loadingOrden = true;
        
        // Usar los datos demo o hacer la llamada real al backend
        const response = await facturacionService.BUSCAR_ORDEN_PUBLICA({
          numero_orden: this.numeroOrden
        });

        if (response.success) {
          this.ordenData = response.data;
          
          // Detectar si la orden ya fue facturada (Req 8.4)
          if (this.ordenData.facturada === true) {
            this.ordenYaFacturada = true;
            this.facturaExistente = this.ordenData.factura || null;
          }
          
          // Precargar datos fiscales si existen
          if (this.ordenData.cliente?.email) {
            this.datosFiscales.email = this.ordenData.cliente.email;
          }
        } else {
          console.error('Error cargando datos de la orden:', response.message);
          this.$router.push({ name: 'buscar-orden' });
        }

      } catch (error) {
        console.error('Error cargando datos de la orden:', error);
        this.$router.push({ name: 'buscar-orden' });
      } finally {
        this.loadingOrden = false;
      }
    },

    async validarRFC() {
      if (!this.datosFiscales.rfc) {
        this.errores = { ...this.errores, rfc: '' };
        return;
      }

      // Validación local según tipo de persona (Req 3.1)
      const resultado = validarFormatoRFC(this.datosFiscales.rfc, this.datosFiscales.tipo_persona);

      if (!resultado.valido) {
        // Req 3.4: Mostrar error sin llamar al backend
        this.errores = { ...this.errores, rfc: 'Formato de RFC inválido' };
        return;
      }

      // Req 3.2: Validación contra el SAT solo si el formato local es válido
      try {
        const response = await facturacionService.VALIDAR_RFC({
          rfc: this.datosFiscales.rfc.toUpperCase()
        });

        if (response.success && response.data?.valido) {
          this.errores = { ...this.errores, rfc: '' };
          // Req 3.3: Precargar razón social y régimen fiscal
          if (response.data.razon_social) {
            this.datosFiscales.razon_social = response.data.razon_social;
          }
          if (response.data.regimen_fiscal) {
            this.datosFiscales.regimen_fiscal = response.data.regimen_fiscal;
          }
        } else {
          // Req 3.5: RFC no válido en el SAT
          this.errores = { ...this.errores, rfc: 'RFC no válido en el SAT' };
        }
      } catch (error) {
        console.error('Error validando RFC:', error);
        this.errores = { ...this.errores, rfc: '' };
      }
    },

    async generarFactura() {
      if (!this.formularioValido) return;

      this.loading = true;
      this.errorGeneracion = '';
      // Limpiar errores de campo previos de validación 422
      this.errores = { ...this.errores, rfc: this.errores.rfc };

      try {
        const response = await facturacionService.CREAR_FACTURA_PUBLICA({
          orden_id: this.ordenData.id,
          datos_fiscales: this.datosFiscales
        });

        if (response.success) {
          this.facturaGenerada = response.data;
        } else {
          const errorClasificado = clasificarErrorFacturacion(response.message);

          if (errorClasificado.tipo === 'validacion') {
            // HTTP 422: mostrar errores por campo (Req 8.1)
            this.errores = { ...this.errores, ...errorClasificado.erroresCampo };
            this.errorGeneracion = '';
          } else {
            // conexion (Req 8.3), servidor (Req 8.2), o general
            this.errorGeneracion = errorClasificado.mensaje;
          }
        }
      } catch (error) {
        console.error('Error generando factura:', error);
        this.errorGeneracion = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      } finally {
        this.loading = false;
      }
    },

    cerrarModal() {
      this.facturaGenerada = null;
      this.emailEnviado = false;
      this.$router.push({ name: 'buscar-orden' });
    },

    descargarArchivo(blob, nombreArchivo) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async descargarPDF() {
      if (!this.facturaGenerada) return;
      this.descargando.pdf = true;
      this.errorDescarga = { ...this.errorDescarga, pdf: '' };
      try {
        const response = await facturacionService.GENERAR_PDF_FACTURA({
          factura_id: this.facturaGenerada.id
        });
        if (response.success && response.data) {
          const blob = response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/pdf' });
          this.descargarArchivo(blob, `factura_${this.facturaGenerada.folio}.pdf`);
        } else {
          this.errorDescarga = { ...this.errorDescarga, pdf: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
        }
      } catch (error) {
        console.error('Error descargando PDF:', error);
        this.errorDescarga = { ...this.errorDescarga, pdf: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
      } finally {
        this.descargando.pdf = false;
      }
    },

    async descargarXML() {
      if (!this.facturaGenerada) return;
      this.descargando.xml = true;
      this.errorDescarga = { ...this.errorDescarga, xml: '' };
      try {
        const response = await facturacionService.GENERAR_PDF_FACTURA({
          factura_id: this.facturaGenerada.id,
          formato: 'xml'
        });
        if (response.success && response.data) {
          const blob = response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/xml' });
          this.descargarArchivo(blob, `factura_${this.facturaGenerada.folio}.xml`);
        } else {
          this.errorDescarga = { ...this.errorDescarga, xml: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
        }
      } catch (error) {
        console.error('Error descargando XML:', error);
        this.errorDescarga = { ...this.errorDescarga, xml: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
      } finally {
        this.descargando.xml = false;
      }
    },

    async enviarEmail() {
      if (!this.facturaGenerada) return;
      this.enviandoEmail = true;
      this.emailEnviado = false;
      this.errorEmail = '';
      try {
        const response = await facturacionService.ENVIAR_FACTURA_EMAIL({
          factura_id: this.facturaGenerada.id,
          email: this.datosFiscales.email
        });
        if (response.success) {
          this.emailEnviado = true;
        } else {
          this.errorEmail = 'Error al enviar la factura por correo. Por favor, intenta nuevamente.';
        }
      } catch (error) {
        console.error('Error enviando email:', error);
        this.errorEmail = 'Error al enviar la factura por correo. Por favor, intenta nuevamente.';
      } finally {
        this.enviandoEmail = false;
      }
    },

    // Métodos para factura existente (orden ya facturada) - Req 8.4
    async descargarPDFExistente() {
      if (!this.facturaExistente) return;
      this.descargando.pdf = true;
      this.errorDescarga = { ...this.errorDescarga, pdf: '' };
      try {
        const response = await facturacionService.GENERAR_PDF_FACTURA({
          factura_id: this.facturaExistente.id
        });
        if (response.success && response.data) {
          const blob = response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/pdf' });
          this.descargarArchivo(blob, `factura_${this.facturaExistente.folio}.pdf`);
        } else {
          this.errorDescarga = { ...this.errorDescarga, pdf: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
        }
      } catch (error) {
        console.error('Error descargando PDF:', error);
        this.errorDescarga = { ...this.errorDescarga, pdf: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
      } finally {
        this.descargando.pdf = false;
      }
    },

    async descargarXMLExistente() {
      if (!this.facturaExistente) return;
      this.descargando.xml = true;
      this.errorDescarga = { ...this.errorDescarga, xml: '' };
      try {
        const response = await facturacionService.GENERAR_PDF_FACTURA({
          factura_id: this.facturaExistente.id,
          formato: 'xml'
        });
        if (response.success && response.data) {
          const blob = response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/xml' });
          this.descargarArchivo(blob, `factura_${this.facturaExistente.folio}.xml`);
        } else {
          this.errorDescarga = { ...this.errorDescarga, xml: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
        }
      } catch (error) {
        console.error('Error descargando XML:', error);
        this.errorDescarga = { ...this.errorDescarga, xml: 'Error al descargar el archivo. Por favor, intenta nuevamente.' };
      } finally {
        this.descargando.xml = false;
      }
    },

    async enviarEmailExistente() {
      if (!this.facturaExistente) return;
      this.enviandoEmail = true;
      this.emailEnviado = false;
      this.errorEmail = '';
      try {
        const response = await facturacionService.ENVIAR_FACTURA_EMAIL({
          factura_id: this.facturaExistente.id,
          email: this.ordenData?.cliente?.email || ''
        });
        if (response.success) {
          this.emailEnviado = true;
        } else {
          this.errorEmail = 'Error al enviar la factura por correo. Por favor, intenta nuevamente.';
        }
      } catch (error) {
        console.error('Error enviando email:', error);
        this.errorEmail = 'Error al enviar la factura por correo. Por favor, intenta nuevamente.';
      } finally {
        this.enviandoEmail = false;
      }
    },

    formatearFecha(fecha) {
      if (!fecha) return '';
      return new Date(fecha).toLocaleDateString('es-MX');
    },

    formatearMonto(monto) {
      if (!monto) return '0.00';
      return parseFloat(monto).toFixed(2);
    }
  },
  watch: {
    'datosFiscales.tipo_persona'(newVal) {
      // Ajustar regímenes fiscales según tipo de persona
      if (newVal === 'fisica') {
        this.datosFiscales.regimen_fiscal = '605'; // Sueldos y Salarios
      } else {
        this.datosFiscales.regimen_fiscal = '601'; // General de Ley Personas Morales
      }
      // Re-validar RFC al cambiar tipo de persona ya que las reglas cambian
      if (this.datosFiscales.rfc) {
        this.errores = { ...this.errores, rfc: '' };
        this.validarRFC();
      }
    }
  }
};
</script>

<style scoped>
/* Estilos adicionales si son necesarios */
</style>
